import React from "react";
import ct from "class-types.macro";
import { cls } from "@wss/cls";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { SelectedProduct } from "~/common/Store/models/ProductListing";
import { User } from "~/common/Store/models/User";
import PlusPriceContainer from "./PlusPriceContainer";
import PlusPriceBox from "./PlusPriceBox";
import SaleBadgeAndPricing from "~/common/SaleBadgeAndPricing";
import DivideByPricing from "./DivideByPricing";

interface Props {
  selectedProduct: SelectedProduct;
  user: User;
  productListingView?: string;
  isBrandAd?: boolean;
}

const PlusMemberDiscount = ({
  selectedProduct,
  productListingView,
  isBrandAd,
}: Props) => {
  const { price, activeSale, unitOfMeasure } = selectedProduct;

  const hasMAPStyle = price.minimumAdvertisedPriceProperties.type.trim();
  const plusPrice = price.minimumAdvertisedPriceOverride.priceLevel5;
  const salePrice = activeSale ? price.salePrice : Infinity;
  const mapPrice = price.minimumAdvertisedPriceProperties.price;

  const getLowestPrice = () => {
    let lowestPrice = Math.min(
      activeSale ? price.salePrice : Infinity,
      price.price,
    );

    if (hasMAPStyle) {
      lowestPrice = Math.min(
        price.minimumAdvertisedPriceProperties.price || Infinity,
        lowestPrice,
      );
    }
    return lowestPrice;
  };

  const isMapPriceLower = getLowestPrice() === mapPrice;
  const isSalePriceLower = getLowestPrice() === salePrice;

  return (
    <PlusPriceContainer
      productListingView={productListingView}
      isBrandAd={isBrandAd}
    >
      <PlusPriceBox isBrandAd={isBrandAd}>
        <p
          className={ct(
            "block",
            "font-semibold",
            "text-xs",
            "leading-none",
            "mb-0",
            "p-0",
            "text-blue-800",
            isBrandAd ? "text-left" : "text-center",
          )}
        >
          Plus Member Price
        </p>
        <p
          data-testid="price"
          className={ct(
            "block",
            "font-semibold",
            "leading-none",
            "mb-0",
            "p-0",
            "relative",
            isBrandAd ? "text-left" : "text-center",
            "text-blue-800",
            productListingView === "list" ? ct("text-xl") : ct("text-2xl"),
          )}
        >
          {formatAsCurrency.formatIfUndefined(plusPrice)}
          <span className="text-xs leading-none text-blue-800">
            /{unitOfMeasure}
          </span>
        </p>
        <DivideByPricing
          dataTestId="divideByPricingPlusDiscount"
          price={
            selectedProduct.price.minimumAdvertisedPriceOverride.priceLevel5
          }
          divideByUnit={selectedProduct.divideByUnit}
          divideQuantity={selectedProduct.divideQuantity}
          additionalClassnames={isBrandAd ? "text-left" : ""}
        />
      </PlusPriceBox>
      {!price.hasQuantityDiscounts && isMapPriceLower && (
        <PriceDescription
          productListingView={productListingView}
          text="Regularly"
          isBrandAd={isBrandAd}
        >
          <PriceText>{formatAsCurrency.formatIfUndefined(mapPrice)}</PriceText>
        </PriceDescription>
      )}
      {!price.hasQuantityDiscounts && isSalePriceLower && (
        <SaleBadgeAndPricing
          dataTestId="itemSale"
          price={salePrice}
          salePricingClassNames={cls(
            "p-0",
            "mb-0",
            "xs:mt-1",
            productListingView === "grid" ? "pt-1" : "pt-2 xs:py-1",
            "text-xs",
            "text-gray-800",
            "font-normal",
            isBrandAd ? "text-left" : "text-center",
          )}
          saleBadgeExtraClassNames={cls("px-1", "mr-1", "py-0.5")}
          labelClassnames="text-red-500 font-bold"
          hideRegularLabel
        />
      )}
      {!price.hasQuantityDiscounts && !isSalePriceLower && !isMapPriceLower && (
        <PriceDescription
          productListingView={productListingView}
          text="Regularly"
          isBrandAd={isBrandAd}
        >
          <PriceText>
            {formatAsCurrency.formatIfUndefined(price.price)}
          </PriceText>
        </PriceDescription>
      )}
    </PlusPriceContainer>
  );
};

function PriceDescription({
  children,
  text,
  productListingView,
  isBrandAd,
}: {
  children: React.ReactNode;
  text?: string;
  productListingView?: string;
  isBrandAd?: boolean;
}) {
  const getClassnames = () => {
    if (isBrandAd) {
      return cls(
        "font-semibold",
        "text-xs",
        "leading-none",
        "m-0",
        "mt-1",
        productListingView === "list" ? "xs:text-center" : "text-left",
      );
    }

    return cls(
      "font-semibold",
      "text-xs",
      "leading-none",
      "m-0",
      "mt-1",
      productListingView === "list" ? "xs:text-center" : "text-center",
    );
  };
  return (
    <p className={getClassnames()}>
      {text} {children}
    </p>
  );
}

function PriceText({ children }: { children: React.ReactNode }) {
  return (
    <span
      className={ct(
        "font-semibold",
        "text-xs",
        "leading-none",
        "mb-0",
        "text-center",
        "text-red-500",
      )}
    >
      {children}
    </span>
  );
}

export default PlusMemberDiscount;
